import { useState } from 'react';
import { usePedidos, useTransicionarEstadoPedido } from '../../hooks/usePedidos';
import { OrderTable } from '../../components/admin/OrderTable';
import { EstadoTransitionButtons } from '../../components/admin/EstadoTransitionButtons';
import { Spinner } from '../../components/shared/Spinner';
import { Card } from '../../components/shared/Card';
import { EstadoPedidoBadge } from '../../components/shared/Badge';
import { EstadoPedido, ETIQUETAS_ESTADO, Pedido } from '../../types/pedido';
import { useToast } from '../../components/shared/Toast';

const FILTROS: Array<{ label: string; estado?: EstadoPedido }> = [
  { label: 'Todos' },
  { label: 'Pendientes', estado: 'PendienteDeConfirmacion' },
  { label: 'Confirmados', estado: 'Confirmado' },
  { label: 'En proceso', estado: 'EnProceso' },
  { label: 'Completados', estado: 'Completado' },
  { label: 'Cancelados', estado: 'Cancelado' },
];

export function PedidosAdminPage() {
  const [filtro, setFiltro] = useState<EstadoPedido | undefined>();
  const { data: pedidos, isLoading } = usePedidos(filtro);
  const transicionar = useTransicionarEstadoPedido();
  const { mostrarError, mostrarExito } = useToast();
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState<Pedido | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2.5">
        {FILTROS.map((f) => (
          <button
            key={f.label}
            onClick={() => setFiltro(f.estado)}
            data-testid={`order-filter-${f.label.toLowerCase()}`}
            className={`px-5 py-2 rounded-full text-xs font-semibold ${
              filtro === f.estado ? 'bg-terracotta text-white' : 'bg-white border border-admin-border text-admin-ink'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <OrderTable pedidos={pedidos ?? []} onVerDetalle={setPedidoSeleccionado} />
      )}

      {pedidoSeleccionado && (
        <Card className="p-7 flex flex-col gap-5">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-lg font-display font-semibold text-wood">
              Pedido de {pedidoSeleccionado.contacto.nombre}
            </h2>
            <EstadoPedidoBadge
              estado={pedidoSeleccionado.estado}
              etiqueta={ETIQUETAS_ESTADO[pedidoSeleccionado.estado]}
            />
          </div>
          <div className="flex flex-wrap gap-4 sm:gap-10 text-[13px]">
            <div>
              <span className="text-admin-ink-muted">Teléfono: </span>
              {pedidoSeleccionado.contacto.telefono}
            </div>
            <div>
              <span className="text-admin-ink-muted">Creado: </span>
              {new Date(pedidoSeleccionado.fechaCreacion).toLocaleString('es-PE')}
            </div>
          </div>
          <div className="flex flex-col gap-2 border-t border-admin-border pt-3.5">
            {pedidoSeleccionado.lineas.map((linea, i) => (
              <div key={i} className="flex justify-between text-[13px]">
                <span>
                  {linea.nombreProductoSnapshot} × {linea.nombrePaqueteSnapshot} ({linea.cantidadDePaquetes})
                </span>
                <span className="font-semibold">S/ {linea.subtotal.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-[15px] font-bold border-t border-admin-border pt-2.5 mt-1">
              <span>Total</span>
              <span>S/ {pedidoSeleccionado.totalPedido.toFixed(2)}</span>
            </div>
          </div>
          <EstadoTransitionButtons
            pedido={pedidoSeleccionado}
            transicionando={transicionar.isPending}
            onTransicionar={(estado) =>
              transicionar.mutate(
                { id: pedidoSeleccionado.id, estado },
                {
                  onSuccess: (actualizado) => {
                    mostrarExito(`Pedido actualizado a "${ETIQUETAS_ESTADO[estado]}"`);
                    setPedidoSeleccionado(actualizado);
                  },
                  onError: (error) => mostrarError((error as Error).message),
                },
              )
            }
          />
        </Card>
      )}
    </div>
  );
}
